/**
 * OpenAI SDK Getter-Proxy Patching Implementation
 * 
 * Implements multiple patching strategies for OpenAI SDK v4.x
 * with fallback chains and comprehensive error handling.
 */

import type { OpenAI } from 'openai'
import type { PatchResult, MiharuInstance } from '@/types/core'
import type { SDKStructureAnalysis } from './analyzer'
import { openaiAnalyzer } from './analyzer'
import { Logger } from '@/utils/logger'
import { MiharuError } from '@/types/core'

const logger = new Logger('OpenAIPatcher')

export interface PatchContext {
  instance: MiharuInstance
  originalMethod: Function
  resourcePath: string
  methodName: string
  patchStrategy: string
}

export interface CallMetadata {
  instanceId: string
  provider: 'openai'
  model: string
  startTime: number
  requestId: string
  isStreaming: boolean
}

export class OpenAISDKPatcher {
  private patchedClients = new WeakSet<OpenAI>()
  private originalMethods = new WeakMap<Function, Function>()
  private patchContexts = new WeakMap<OpenAI, Map<string, PatchContext>>()

  /**
   * Main entry point for patching an OpenAI client
   */
  async patchClient(client: OpenAI, instance: MiharuInstance): Promise<PatchResult> {
    if (this.patchedClients.has(client)) {
      return {
        success: true,
        method: 'already-patched',
        healthCheck: true
      }
    }

    try {
      // Analyze SDK structure first
      const analysis = await openaiAnalyzer.analyzeSDKStructure(client)
      
      if (!analysis.compatibility.canPatch) {
        throw new MiharuError(
          `Cannot patch OpenAI client: ${analysis.compatibility.issues.join(', ')}`,
          'PATCH_INCOMPATIBLE'
        )
      }

      logger.info(`Patching OpenAI SDK v${analysis.version} using multiple strategies`)

      // Initialize patch context map for this client
      const contextMap = new Map<string, PatchContext>()
      this.patchContexts.set(client, contextMap)

      // Apply patches for each endpoint
      const patchResults = await Promise.all([
        this.patchEndpoint(client, instance, analysis, 'chat.completions', 'create'),
        this.patchEndpoint(client, instance, analysis, 'completions', 'create'),
        this.patchEndpoint(client, instance, analysis, 'embeddings', 'create')
      ])

      // Check if at least one critical patch succeeded
      const chatPatchSuccess = patchResults[0]
      if (!chatPatchSuccess) {
        throw new MiharuError(
          'Failed to patch critical chat.completions.create method',
          'PATCH_CRITICAL_FAILURE'
        )
      }

      this.patchedClients.add(client)

      return {
        success: true,
        method: 'multi-strategy',
        strategy: analysis.patchingPoints.chat.completions.patchStrategy,
        healthCheck: await this.performHealthCheck(client)
      }

    } catch (error) {
      logger.error('OpenAI client patching failed', error)
      
      return {
        success: false,
        method: 'multi-strategy',
        error: error instanceof Error ? error.message : 'Unknown patching error'
      }
    }
  }

  /**
   * Patch a specific endpoint using the optimal strategy
   */
  private async patchEndpoint(
    client: OpenAI,
    instance: MiharuInstance,
    analysis: SDKStructureAnalysis,
    resourcePath: string,
    methodName: string
  ): Promise<boolean> {
    const patchingPoint = this.getPatchingPoint(analysis, resourcePath)
    
    if (!patchingPoint?.accessible) {
      logger.warn(`Endpoint ${resourcePath}.${methodName} not accessible, skipping`)
      return false
    }

    try {
      switch (patchingPoint.patchStrategy) {
        case 'getter-proxy':
          return await this.applyGetterProxyPatch(client, instance, resourcePath, methodName)
        case 'method-override':
          return await this.applyMethodOverridePatch(client, instance, resourcePath, methodName)
        case 'prototype-patch':
          return await this.applyPrototypePatch(client, instance, resourcePath, methodName)
        default:
          logger.warn(`Unknown patch strategy: ${patchingPoint.patchStrategy}`)
          return false
      }
    } catch (error) {
      logger.error(`Failed to patch ${resourcePath}.${methodName}`, error)
      return false
    }
  }

  /**
   * Apply getter-proxy based patching (most reliable for OpenAI v4.0-4.30)
   */
  private async applyGetterProxyPatch(
    client: OpenAI,
    instance: MiharuInstance,
    resourcePath: string,
    methodName: string
  ): Promise<boolean> {
    try {
      const resource = this.navigateToResource(client, resourcePath)
      if (!resource) return false

      const originalMethod = resource[methodName]
      if (typeof originalMethod !== 'function') return false

      // Store original method for restoration
      this.originalMethods.set(originalMethod, originalMethod)

      // Create wrapper function with monitoring
      const wrappedMethod = this.createMonitoringWrapper(
        originalMethod,
        instance,
        resourcePath,
        methodName
      )

      // Apply getter-proxy patch
      Object.defineProperty(resource, methodName, {
        get: () => wrappedMethod,
        configurable: true,
        enumerable: true
      })

      // Store patch context
      const contextMap = this.patchContexts.get(client)!
      contextMap.set(`${resourcePath}.${methodName}`, {
        instance,
        originalMethod,
        resourcePath,
        methodName,
        patchStrategy: 'getter-proxy'
      })

      logger.debug(`Getter-proxy patch applied to ${resourcePath}.${methodName}`)
      return true

    } catch (error) {
      logger.error(`Getter-proxy patch failed for ${resourcePath}.${methodName}`, error)
      return false
    }
  }

  /**
   * Apply method override patching (fallback strategy)
   */
  private async applyMethodOverridePatch(
    client: OpenAI,
    instance: MiharuInstance,
    resourcePath: string,
    methodName: string
  ): Promise<boolean> {
    try {
      const resource = this.navigateToResource(client, resourcePath)
      if (!resource) return false

      const originalMethod = resource[methodName]
      if (typeof originalMethod !== 'function') return false

      // Store original method
      this.originalMethods.set(originalMethod, originalMethod)

      // Create and apply wrapper
      const wrappedMethod = this.createMonitoringWrapper(
        originalMethod,
        instance,
        resourcePath,
        methodName
      )

      resource[methodName] = wrappedMethod

      // Store patch context
      const contextMap = this.patchContexts.get(client)!
      contextMap.set(`${resourcePath}.${methodName}`, {
        instance,
        originalMethod,
        resourcePath,
        methodName,
        patchStrategy: 'method-override'
      })

      logger.debug(`Method override patch applied to ${resourcePath}.${methodName}`)
      return true

    } catch (error) {
      logger.error(`Method override patch failed for ${resourcePath}.${methodName}`, error)
      return false
    }
  }

  /**
   * Apply prototype patching (for newer SDK versions)
   */
  private async applyPrototypePatch(
    client: OpenAI,
    instance: MiharuInstance,
    resourcePath: string,
    methodName: string
  ): Promise<boolean> {
    try {
      const resource = this.navigateToResource(client, resourcePath)
      if (!resource) return false

      const prototype = Object.getPrototypeOf(resource)
      if (!prototype) return false

      const originalMethod = prototype[methodName]
      if (typeof originalMethod !== 'function') return false

      // Store original method
      this.originalMethods.set(originalMethod, originalMethod)

      // Create and apply wrapper to prototype
      const wrappedMethod = this.createMonitoringWrapper(
        originalMethod,
        instance,
        resourcePath,
        methodName
      )

      prototype[methodName] = wrappedMethod

      // Store patch context
      const contextMap = this.patchContexts.get(client)!
      contextMap.set(`${resourcePath}.${methodName}`, {
        instance,
        originalMethod,
        resourcePath,
        methodName,
        patchStrategy: 'prototype-patch'
      })

      logger.debug(`Prototype patch applied to ${resourcePath}.${methodName}`)
      return true

    } catch (error) {
      logger.error(`Prototype patch failed for ${resourcePath}.${methodName}`, error)
      return false
    }
  }

  /**
   * Create monitoring wrapper function
   */
  private createMonitoringWrapper(
    originalMethod: Function,
    instance: MiharuInstance,
    resourcePath: string,
    methodName: string
  ): Function {
    return async function(this: any, ...args: any[]) {
      const startTime = Date.now()
      const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      // Extract call metadata
      const metadata: CallMetadata = {
        instanceId: instance.id,
        provider: 'openai',
        model: args[0]?.model || 'unknown',
        startTime,
        requestId,
        isStreaming: args[0]?.stream === true
      }

      logger.debug(`Intercepted ${resourcePath}.${methodName}`, {
        model: metadata.model,
        streaming: metadata.isStreaming,
        requestId: metadata.requestId
      })

      try {
        // Update instance stats
        instance.incrementCallCount()

        // Call original method
        const result = await originalMethod.apply(this, args)

        // Handle streaming vs non-streaming results
        if (metadata.isStreaming && result && typeof result[Symbol.asyncIterator] === 'function') {
          return createStreamingWrapper(result, metadata, instance)
        } else {
          // Handle non-streaming result
          const endTime = Date.now()
          const latency = endTime - startTime

          // Extract tokens and cost from result
          const tokens = result?.usage?.total_tokens || 0
          const cost = calculateCost(metadata.model, result?.usage)

          // Update instance metrics
          instance.addTokens(tokens)
          instance.addCost(cost)

          logger.debug(`Call completed`, {
            requestId: metadata.requestId,
            latency,
            tokens,
            cost
          })

          return result
        }

      } catch (error) {
        const endTime = Date.now()
        const latency = endTime - startTime

        // Update error stats
        instance.incrementErrors()

        logger.error(`Call failed`, {
          requestId: metadata.requestId,
          latency,
          error: error instanceof Error ? error.message : 'Unknown error'
        })

        throw error
      }
    }
  }

  /**
   * Navigate to a resource using dot notation path
   */
  private navigateToResource(client: OpenAI, resourcePath: string): any {
    const parts = resourcePath.split('.')
    let current: any = client

    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part]
      } else {
        return null
      }
    }

    return current
  }

  /**
   * Get patching point configuration from analysis
   */
  private getPatchingPoint(analysis: SDKStructureAnalysis, resourcePath: string) {
    switch (resourcePath) {
      case 'chat.completions':
        return analysis.patchingPoints.chat.completions
      case 'completions':
        return analysis.patchingPoints.completions
      case 'embeddings':
        return analysis.patchingPoints.embeddings
      default:
        return null
    }
  }

  /**
   * Perform health check on patched client
   */
  private async performHealthCheck(client: OpenAI): Promise<boolean> {
    try {
      // Verify that chat.completions.create is still callable
      const method = client.chat?.completions?.create
      return typeof method === 'function'
    } catch (error) {
      logger.warn('Health check failed', error)
      return false
    }
  }

  /**
   * Remove patches from a client
   */
  unpatchClient(client: OpenAI): boolean {
    if (!this.patchedClients.has(client)) {
      return true // Already unpatched
    }

    try {
      const contextMap = this.patchContexts.get(client)
      if (!contextMap) return true

      // Restore original methods
      for (const [_key, context] of contextMap) {
        this.restoreOriginalMethod(client, context)
      }

      this.patchedClients.delete(client)
      this.patchContexts.delete(client)

      logger.info('OpenAI client unpatched successfully')
      return true

    } catch (error) {
      logger.error('Failed to unpatch OpenAI client', error)
      return false
    }
  }

  /**
   * Restore original method for a specific context
   */
  private restoreOriginalMethod(client: OpenAI, context: PatchContext): void {
    try {
      const resource = this.navigateToResource(client, context.resourcePath)
      if (!resource) return

      switch (context.patchStrategy) {
        case 'getter-proxy':
          // Remove getter and restore original method
          delete resource[context.methodName]
          resource[context.methodName] = context.originalMethod
          break
        case 'method-override':
          // Directly restore method
          resource[context.methodName] = context.originalMethod
          break
        case 'prototype-patch':
          // Restore prototype method
          const prototype = Object.getPrototypeOf(resource)
          if (prototype) {
            prototype[context.methodName] = context.originalMethod
          }
          break
      }
    } catch (error) {
      logger.warn(`Failed to restore ${context.resourcePath}.${context.methodName}`, error)
    }
  }

  /**
   * Check if client is patched
   */
  isPatched(client: OpenAI): boolean {
    return this.patchedClients.has(client)
  }
}

/**
 * Create streaming wrapper for async iterators
 */
function createStreamingWrapper(
  stream: AsyncIterable<any>,
  metadata: CallMetadata,
  instance: MiharuInstance
) {
  const wrappedStream = async function* () {
    let chunkCount = 0
    let totalTokens = 0
    let content = ''

    try {
      for await (const chunk of stream) {
        chunkCount++
        
        // Accumulate streaming data
        if (chunk.choices?.[0]?.delta?.content) {
          content += chunk.choices[0].delta.content
        }

        // Extract token information if available
        if (chunk.usage?.total_tokens) {
          totalTokens = chunk.usage.total_tokens
        }

        yield chunk
      }

      // Update final metrics
      const endTime = Date.now()
      const latency = endTime - metadata.startTime
      const cost = calculateCost(metadata.model, { total_tokens: totalTokens })

      instance.addTokens(totalTokens)
      instance.addCost(cost)

      logger.debug(`Stream completed`, {
        requestId: metadata.requestId,
        chunks: chunkCount,
        tokens: totalTokens,
        latency,
        cost
      })

    } catch (error) {
      instance.incrementErrors()
      logger.error(`Stream failed`, {
        requestId: metadata.requestId,
        chunks: chunkCount,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw error
    }
  }

  // Preserve async iterator symbol
  wrappedStream[Symbol.asyncIterator] = wrappedStream

  return wrappedStream()
}

/**
 * Calculate cost based on model and usage
 */
function calculateCost(model: string, usage: any): number {
  if (!usage?.total_tokens) return 0

  // Simplified cost calculation (should be configurable)
  const costPerToken = {
    'gpt-4': 0.00003,
    'gpt-4-turbo': 0.00001,
    'gpt-3.5-turbo': 0.000002,
  }

  const rate = costPerToken[model as keyof typeof costPerToken] || 0.00001
  return usage.total_tokens * rate
}

// Export singleton instance
export const openaiPatcher = new OpenAISDKPatcher()